import { useEffect, useState } from "react";
import axios from "axios";
import * as Yup from "yup";
import { API_URL } from "../../../constants";
import {TextField, Grid, MenuItem, CircularProgress, Snackbar, Alert, Box, Typography, useTheme} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useFormik } from "formik";
import ButtonForm from "../../../components/Button/ButtonForm";
import CardMarkerSmall from "../../../components/CockpitCards/CardMarkerSmall";
import ShippingAddressEditor from "../../../components/Address/ShippingAddressEditor";
import { useTranslation } from "../../../language";

const usernameRegExp = /^[\w.@+-]+$/;
const phoneRegExp = /^\+?[1-9]\d{1,14}$/;

const profileValidationSchema = Yup.object({
  first_name: Yup.string().max(150),
  last_name: Yup.string().max(150),
  username: Yup.string()
    .matches(usernameRegExp, "Username may contain only letters, digits, and @/./+/-/_ characters")
    .min(3, "Minimum 3 characters")
    .max(150)
    .required("Username is required"),
  email: Yup.string()
    .email("Enter a valid email address")
    .max(254, "Email cannot exceed 254 characters")
    .required("Email is required"),
  phone_number: Yup.string()
    .matches(phoneRegExp, "Enter a valid phone number (e.g., +123456789)")
    .nullable(),
  language: Yup.string().oneOf(["en", "de"]),
  height_cm: Yup.number()
    .min(50, "Too small")
    .max(300, "Too tall")
    .nullable(),
});


export default function ProfilePage() {

  const theme = useTheme();
  const { t } = useTranslation();
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    async function fetchProfile() {
      try {
        const res = await axios.get(`${API_URL}/api/auth/me/`);
        setInitialValues(res.data);
      } catch (err) {
        setErrorMessage(t('ua_profile_load_error'));
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues || {
      first_name: "",
      last_name: "",
      username: "",
      email: "",
      phone_number: "",
      language: "en",
      height_cm: "",
    },
    validationSchema: profileValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await axios.patch(`${API_URL}/api/auth/me/`, values);
        setSuccessOpen(true);
      } catch (err) {
        setErrorMessage(
          err.response?.data?.detail || t('ua_profile_update_error')
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  const isDark = theme.palette.mode === 'dark';
  const glassCardSx = {
    bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
    backdropFilter: 'blur(28px) saturate(180%)',
    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
    boxShadow: isDark
      ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
      : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
  };

  return (
     <Grid size={{ xs: 12 }} sx={{ maxWidth: 800, width: '100%', px: { xs: 2, sm: 0 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>

        <Box sx={{ width: '100%', mb: 4 }}>
        <CardMarkerSmall

        headline={t('ua_profile_headline')}
        subtext={t('ua_profile_subtext')}
        sx={glassCardSx}

        cardlines={<>

        <form onSubmit={formik.handleSubmit}>
          <Grid container>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                label={t('ua_profile_first_name')}
                {...formik.getFieldProps("first_name")}
                error={formik.touched.first_name && Boolean(formik.errors.first_name)}
                helperText={formik.touched.first_name && formik.errors.first_name}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                label={t('ua_profile_last_name')}
                {...formik.getFieldProps("last_name")}
                error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                helperText={formik.touched.last_name && formik.errors.last_name}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                label={t('ua_profile_username')}
                {...formik.getFieldProps("username")}
                error={formik.touched.username && Boolean(formik.errors.username)}
                helperText={formik.touched.username && formik.errors.username}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                label={t('ua_profile_email')}
                type="email"
                {...formik.getFieldProps("email")}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                label={t('ua_profile_phone')}
                placeholder={t('ua_profile_phone_ph')}
                {...formik.getFieldProps("phone_number")}
                error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
                helperText={formik.touched.phone_number && formik.errors.phone_number}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                select
                label={t('ua_profile_language')}
                {...formik.getFieldProps("language")}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="de">Deutsch</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                label={t('ua_profile_height')}
                {...formik.getFieldProps("height_cm")}
                error={formik.touched.height_cm && Boolean(formik.errors.height_cm)}
                helperText={formik.touched.height_cm && formik.errors.height_cm}
              />
            </Grid>

          <Grid size={12} sx={{my:1, display: "flex", justifyContent: "center", alignItems: "center"}}>
            <ButtonForm
              disabled={!formik.dirty || formik.isSubmitting}
              startIcon={formik.isSubmitting && (<CircularProgress size={18} />)}
              name={t('ua_profile_save')}
            />
          </Grid>
        </Grid>

        </form>

      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
          <Typography variant="h6">{t('ua_profile_success')}</Typography>
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={5000}
        onClose={() => setErrorMessage("")}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
          <Typography variant="h6">{errorMessage}</Typography>
        </Alert>
      </Snackbar>

       </>}
    />
        </Box>

        <Box sx={{ width: '100%', mb: 4 }}>
      <CardMarkerSmall
        headline={t('ua_address_card_headline')}
        subtext={t('ua_address_card_subtext')}
        sx={glassCardSx}
        cardlines={
          <ShippingAddressEditor
            labelKey="ua_address_label"
            promptKey="ua_address_change_prompt"
            emptyKey="ua_address_empty"
          />
        }
      />
        </Box>

      </Grid>
  );
}
